import React, {
 useId, useCallback, useEffect, useMemo 
} from 'react';
import { useTheme } from '@mui/material';
import { map } from './core/MapView';
import getSpeedColor from '../common/util/colors';
import { findFonts } from './core/mapUtil';
import { SpeedLegendControl } from './legend/MapSpeedLegend';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useAttributePreference } from '../common/util/preferences';

const MapRoutePoints = ({ positions, onClick, showSpeedControl }) => {
  const id = useId();
  const theme = useTheme();
  const t = useTranslation();
  const speedUnit = useAttributePreference('speedUnit');

  const onMouseEnter = () => map.getCanvas().style.cursor = 'pointer';
  const onMouseLeave = () => map.getCanvas().style.cursor = '';

  const onMarkerClick = useCallback((event) => {
    event.preventDefault();
    const feature = event.features[0];
    if (onClick) {
      onClick(feature.properties.id, feature.properties.index);
    }
  }, [onClick]);

  useEffect(() => {
    map.addSource(id, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    map.addLayer({
      id,
      type: 'symbol',
      source: id,
      paint: {
        'text-color': ['get', 'color'],
      },
      layout: {
        'text-font': findFonts(map),
         'text-size': 12,
        'text-field': '▲','text-size': 20,
        'text-allow-overlap': true,
        'text-rotate': ['get', 'rotation'],
      },
    });

    map.on('mouseenter', id, onMouseEnter);
    map.on('mouseleave', id, onMouseLeave);
    map.on('click', id, onMarkerClick);

    return () => {
      map.off('mouseenter', id, onMouseEnter);
      map.off('mouseleave', id, onMouseLeave);
      map.off('click', id, onMarkerClick);

      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
      if (map.getSource(id)) {
        map.removeSource(id);
      }
    };
  }, [onMarkerClick]);

  // 2ECOMP MODIFIED: Single-pass para min/max speed (evita 2 iterações)
  const { minSpeed, maxSpeed } = useMemo(() => {
    if (!positions.length) return { minSpeed: 0, maxSpeed: 0 };
    return positions.reduce((acc, p) => ({
      minSpeed: Math.min(acc.minSpeed, p.speed),
      maxSpeed: Math.max(acc.maxSpeed, p.speed),
    }), { minSpeed: Infinity, maxSpeed: -Infinity });
  }, [positions]);

  // 2ECOMP MODIFIED: Memoiza criação de features com validação de coordenadas
  const features = useMemo(() => positions
    .filter((position) => Number.isFinite(position.longitude) && Number.isFinite(position.latitude))
    .map((position, index) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [position.longitude, position.latitude],
      },
      properties: {
        index,
        id: position.id,
        rotation: Number.isFinite(position.course) ? position.course : 0,
        color: getSpeedColor(position.speed, minSpeed, maxSpeed),
      },
    })), [positions, minSpeed, maxSpeed]);

  useEffect(() => {
    const control = new SpeedLegendControl(positions, speedUnit, t, maxSpeed, minSpeed);
    if (showSpeedControl) {
      map.addControl(control, theme.direction === 'rtl' ? 'bottom-right' : 'bottom-left');
    }

    map.getSource(id)?.setData({
      type: 'FeatureCollection',
      features,
    });
    return () => map.removeControl(control);
  }, [id, features, positions, speedUnit, t, maxSpeed, minSpeed, showSpeedControl, theme.direction]);

  return null;
};

// 2ECOMP MODIFIED: React.memo para evitar re-renders desnecessários
export default React.memo(MapRoutePoints);
